import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterX, LayoutList, Lock, Table2 } from 'lucide-react';

import { companiesAPI, trucksAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { fleetReportsAPI, messageErreurRapport } from '../lib/fleetReportsApi';
import { truckOption } from '../lib/selectOptions';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { ACTIONS_COMPACTES, PageHeader } from '../components/ui/page-header';
import { cn } from '../lib/utils';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../components/ui/sonner';
import ApercuMois from '../components/reports/ApercuMois';
import ClosureBanner from '../components/reports/ClosureBanner';
import ExportMenu from '../components/reports/ExportMenu';
import MonthlyBalanceReport from '../components/reports/MonthlyBalanceReport';
import MonthSelector from '../components/reports/MonthSelector';
import OperatingAccountReport from '../components/reports/OperatingAccountReport';
import RentedTrucksReport from '../components/reports/RentedTrucksReport';
import TruckSummaryReport from '../components/reports/TruckSummaryReport';
import { useFleetReport } from '../components/reports/useFleetReport';
import {
  alertesRapport,
  alerteSaisiesTardives,
  chiffresCles,
  statsRapport,
} from '../components/reports/enteteRapport';
import {
  ONGLETS,
  cleMois,
  decaler,
  libelleMois,
  lireMois,
  moisCourant,
  ongletValide,
} from '../components/reports/reportHelpers';

/**
 * Rapports de la flotte (lot 020), réservés à la direction (`canSeeMoney`).
 *
 * Quatre onglets pilotés par l'adresse:
 *   ?onglet=compte | synthese | loues | bilan
 *   ?mois=AAAA-MM            le mois affiché (par défaut le mois en cours)
 *   ?camion=<id>             compte d'exploitation d'un seul camion
 *   ?transporteur=<id>       compte d'exploitation d'un transporteur (relevé propriétaire)
 *   ?vue=tableau             compte d'exploitation en tableau dense (sinon par camion)
 *
 * Une seule bande sous l'en-tête porte le mois, son aperçu (recettes et
 * charges, marge, évolution), son état de clôture et le téléchargement.
 * Chaque libellé arrive déjà en français.
 */

const lirePeriode = (params) => {
  const direct = lireMois(params.get('mois'));
  if (direct) return direct;
  const annee = params.get('annee');
  const mois = params.get('mois');
  if (annee && mois) {
    const composee = lireMois(`${annee}-${mois}`);
    if (composee) return composee;
  }
  return moisCourant();
};

const chargerListe = async (getPage) => {
  try {
    const { items } = await fetchAllPages(getPage);
    return items;
  } catch {
    return [];
  }
};

// Paramètres d'adresse de la page courante de chaque liste de rapport (voir `usePagination`, option `cle`).
const PAGES_DES_LISTES = ['classement-page', 'exploitation-page', 'operations-page', 'loues-page', 'papiers-page'];

const ReportsPage = () => {
  const { canSeeMoney } = useAuth();
  const [params, setParams] = useSearchParams();

  const onglet = ongletValide(params.get('onglet'));
  const periode = lirePeriode(params);
  const camionId = params.get('camion') || '';
  const transporteurId = params.get('transporteur') || '';
  const disposition = params.get('vue') === 'tableau' ? 'tableau' : 'camions';

  const [camions, setCamions] = useState([]);
  const [transporteurs, setTransporteurs] = useState([]);
  const [cloture, setCloture] = useState(false);

  const modifierAdresse = useCallback(
    (changements, { remplacer = true } = {}) => {
      const suivant = new URLSearchParams(params);
      Object.entries(changements).forEach(([cle, valeur]) => {
        if (valeur) suivant.set(cle, valeur);
        else suivant.delete(cle);
      });
      suivant.delete('annee');
      // Un autre mois, onglet, filtre ou disposition ramène chaque liste à sa première page.
      PAGES_DES_LISTES.forEach((nom) => suivant.delete(nom));
      setParams(suivant, { replace: remplacer });
    },
    [params, setParams],
  );

  // Les listes de choix (camions, transporteurs) se chargent une fois, en entier.
  useEffect(() => {
    if (!canSeeMoney) return undefined;
    let annule = false;
    chargerListe((page) => trucksAPI.getAllPaged(page)).then((liste) => !annule && setCamions(liste));
    chargerListe((page) => companiesAPI.getAllPaged(page)).then((liste) => !annule && setTransporteurs(liste));
    return () => {
      annule = true;
    };
  }, [canSeeMoney]);

  const options = useMemo(
    () => ({ annee: periode.annee, mois: periode.mois, camionId, transporteurId }),
    [periode.annee, periode.mois, camionId, transporteurId],
  );

  const cleRapport = `${onglet}|${cleMois(periode)}|${onglet === 'compte' ? `${camionId}|${transporteurId}` : ''}`;
  const rapport = useFleetReport(cleRapport, () => fleetReportsAPI.lire(onglet, options), {
    actif: Boolean(canSeeMoney),
  });
  // Le même rapport du mois précédent, pour l'évolution (recettes, charges, résultat).
  const avecEvolution = onglet === 'compte' || onglet === 'synthese';
  const periodePrecedente = decaler(periode, -1);
  const optionsPrecedentes = useMemo(
    () => ({ ...options, annee: periodePrecedente.annee, mois: periodePrecedente.mois }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, periodePrecedente.annee, periodePrecedente.mois],
  );
  const precedent = useFleetReport(
    `prec|${cleRapport}`,
    () => fleetReportsAPI.lire(onglet, optionsPrecedentes),
    { actif: Boolean(canSeeMoney) && avecEvolution },
  );
  const statut = useFleetReport(
    cleMois(periode),
    () => fleetReportsAPI.statutCloture({ annee: periode.annee, mois: periode.mois }),
    { actif: Boolean(canSeeMoney) },
  );

  const optionsCamions = useMemo(
    () => [{ value: '', label: 'Tous les camions' }, ...camions.map(truckOption)],
    [camions],
  );
  const optionsTransporteurs = useMemo(
    () => [
      { value: '', label: 'Tous les transporteurs' },
      ...transporteurs.map((societe) => ({
        value: societe.id,
        label: societe.name || 'Société sans nom',
        keywords: [societe.city],
      })),
    ],
    [transporteurs],
  );
  const proprietaires = useMemo(() => {
    const noms = new Map();
    transporteurs.forEach((societe) => {
      if (societe.name && societe.id) noms.set(societe.name, societe.id);
    });
    return noms;
  }, [transporteurs]);

  const cloturer = async () => {
    setCloture(true);
    try {
      const resultat = await fleetReportsAPI.cloturer({ annee: periode.annee, mois: periode.mois });
      toast.success(
        resultat?.created === false
          ? 'Ce mois était déjà clôturé.'
          : `${libelleMois(periode)} est clôturé.`,
      );
      await statut.recharger();
    } catch (error) {
      toast.error(await messageErreurRapport(error, "Le mois n'a pas pu être clôturé."));
    } finally {
      setCloture(false);
    }
  };

  if (!canSeeMoney) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <PageHeader eyebrow="Rapports" title="Rapports de la flotte" />
        <EmptyState
          icon={Lock}
          title="Ces montants sont réservés à la direction"
          description="Les rapports de la flotte montrent les recettes, les charges et les résultats. Demandez à la direction de la société de vous les transmettre."
        />
      </div>
    );
  }

  // Les chiffres et les alertes du rapport vivent dans le coin de l'en-tête.
  const rapportPret = rapport.chargement || rapport.erreur ? null : rapport.data;
  const cles = chiffresCles(onglet, rapportPret);
  const clesPrecedentes = avecEvolution && !precedent.chargement && !precedent.erreur
    ? chiffresCles(onglet, precedent.data)
    : null;
  const libellePrecedent = libelleMois(periodePrecedente);
  const { stats: statsEntete, plus: lignesPlus } = statsRapport(
    onglet,
    rapportPret,
    clesPrecedentes,
    libellePrecedent,
  );
  const alertesEntete = [
    ...alertesRapport(onglet, rapport.chargement || rapport.erreur ? null : rapport.data),
    alerteSaisiesTardives(statut.data),
  ].filter(Boolean);
  const statsPlus = lignesPlus ? (
    <dl className="space-y-1.5 text-sm">
      {lignesPlus.map(([libelleLigne, valeur]) => (
        <div key={libelleLigne} className="flex items-baseline justify-between gap-3">
          <dt className="text-muted-foreground">{libelleLigne}</dt>
          <dd className="text-right font-medium tabular-nums">{valeur}</dd>
        </div>
      ))}
    </dl>
  ) : undefined;

  const filtreActif = onglet === 'compte' && (camionId || transporteurId);
  const data = rapport.data;

  const contenu = () => {
    if (rapport.chargement) {
      return <LoadingState shape="table" label="Chargement du rapport" rows={6} columns={6} />;
    }
    if (rapport.erreur) {
      return (
        <EmptyState
          variant="error"
          title="Rapport indisponible"
          description={rapport.erreur}
          actionLabel="Réessayer"
          onAction={rapport.recharger}
        />
      );
    }
    switch (onglet) {
      case 'synthese':
        return <TruckSummaryReport report={data} />;
      case 'loues':
        return <RentedTrucksReport report={data} periode={periode} proprietaires={proprietaires} />;
      case 'bilan':
        return <MonthlyBalanceReport report={data} />;
      default:
        return <OperatingAccountReport report={data} disposition={disposition} />;
    }
  };

  const filtresCompte = onglet === 'compte' && (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtres du compte d'exploitation">
      <Label htmlFor="rapport-camion" className="sr-only">
        Camion
      </Label>
      <div className="w-full sm:w-44">
        <SearchableSelect
          id="rapport-camion"
          value={camionId}
          onValueChange={(valeur) => modifierAdresse({ camion: valeur })}
          options={optionsCamions}
          placeholder="Tous les camions"
          searchPlaceholder="Chercher une plaque ou un chauffeur"
          emptyMessage="Aucun camion trouvé"
        />
      </div>
      <Label htmlFor="rapport-transporteur" className="sr-only">
        Transporteur
      </Label>
      <div className="w-full sm:w-44">
        <SearchableSelect
          id="rapport-transporteur"
          value={transporteurId}
          onValueChange={(valeur) => modifierAdresse({ transporteur: valeur })}
          options={optionsTransporteurs}
          placeholder="Tous les transporteurs"
          searchPlaceholder="Chercher un transporteur"
          emptyMessage="Aucun transporteur trouvé"
        />
      </div>
      {filtreActif && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => modifierAdresse({ camion: '', transporteur: '' })}
        >
          <FilterX aria-hidden="true" />
          Effacer
        </Button>
      )}
      <div className="flex rounded-md border border-border p-0.5" role="group" aria-label="Disposition">
        {[
          { value: 'camions', label: 'Par camion', icone: LayoutList },
          { value: 'tableau', label: 'Tableau', icone: Table2 },
        ].map(({ value, label, icone: Icone }) => (
          <button
            key={value}
            type="button"
            aria-pressed={disposition === value}
            onClick={() => modifierAdresse({ vue: value === 'tableau' ? 'tableau' : '' })}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              disposition === value ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icone className="h-3.5 w-3.5" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-4 md:px-6 md:py-5 lg:px-8">
      <PageHeader
        eyebrow="Rapports"
        title="Rapports de la flotte"
        description={`Ce que chaque camion a rapporté et coûté, mois par mois.${data?.organization?.name ? ` ${data.organization.name}, montants en ${data.currency_label || 'FCFA'}.` : ''}`}
        stats={statsEntete}
        statsPlus={statsPlus}
        alertesSousTitre
        alertes={alertesEntete}
      />

      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border bg-card px-3 py-2 shadow-sm lg:flex-nowrap"
        data-testid="bande-mois"
      >
        <MonthSelector periode={periode} onChange={(suivant) => modifierAdresse({ mois: cleMois(suivant) })} />
        {cles && (
          <ApercuMois
            recettes={cles.recettes}
            charges={cles.charges}
            resultat={cles.resultat}
            precedent={clesPrecedentes}
            libellePrecedent={libellePrecedent}
            className="max-sm:order-last max-sm:w-full sm:flex-1"
          />
        )}
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
          <ClosureBanner
            periode={periode}
            statut={statut.data}
            chargement={statut.chargement}
            onCloturer={cloturer}
            enCours={cloture}
          />
          <div className={ACTIONS_COMPACTES}>
            <ExportMenu etat={onglet} options={options} disabled={rapport.chargement || Boolean(rapport.erreur)} />
          </div>
        </div>
      </div>

      <Tabs value={onglet} onValueChange={(valeur) => modifierAdresse({ onglet: valeur }, { remplacer: false })}>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <TabsList className="flex h-auto max-w-full justify-start gap-1 overflow-x-auto">
            {ONGLETS.map((entree) => (
              <TabsTrigger key={entree.value} value={entree.value} className="shrink-0 px-3 py-1.5">
                {entree.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {filtresCompte}
        </div>

        <TabsContent value={onglet} className="mt-4 space-y-4">
          {contenu()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
